
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Shield, ChevronRight, Check, FileCheck, ClipboardList } from 'lucide-react';
import { Link } from 'react-router-dom';

const WorkersCompService = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Workers' Compensation Insurance through PEOs | The PEO App</title>
        <meta name="description" content="Leverage the power of PEOs to access cost-effective workers' compensation insurance, simplify claims management, and implement safety programs across all 50 states." />
        <link rel="canonical" href="https://thepeoapp.com/services/workers-comp" />
        <meta name="keywords" content="PEO workers compensation, professional employer organization insurance, workers comp claims management, workplace safety programs, affordable workers comp, experience modification rate, EMR improvement" />
      </Helmet>
      
      <Navbar />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-emerald-50 to-green-100 py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center">
              <div className="md:w-1/2 mb-10 md:mb-0 md:pr-8">
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Workers' Compensation Through PEOs</h1>
                <p className="text-xl text-gray-700 mb-8">Access affordable coverage, streamline claims management, and implement effective safety programs—all through a single, integrated solution.</p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button asChild size="lg" className="bg-[#00a86b] hover:bg-[#008f5a] text-white rounded-full">
                    <Link to="/application">Get Started Today <ChevronRight className="ml-2 h-5 w-5" /></Link>
                  </Button>
                </div>
              </div>
              <div className="md:w-1/2">
                <div className="bg-white p-8 rounded-xl shadow-xl">
                  <div className="flex items-center mb-6">
                    <Shield className="h-10 w-10 text-[#00a86b] mr-4" />
                    <h2 className="text-2xl font-bold text-gray-900">Why Choose a PEO for Workers' Comp?</h2>
                  </div>
                  <ul className="space-y-4">
                    <li className="flex">
                      <Check className="h-6 w-6 text-[#00a86b] mr-2 flex-shrink-0" />
                      <span className="text-gray-700">Access to more favorable rates through group purchasing power</span>
                    </li>
                    <li className="flex">
                      <Check className="h-6 w-6 text-[#00a86b] mr-2 flex-shrink-0" />
                      <span className="text-gray-700">Streamlined claims administration and processing</span>
                    </li>
                    <li className="flex">
                      <Check className="h-6 w-6 text-[#00a86b] mr-2 flex-shrink-0" />
                      <span className="text-gray-700">Expert safety programs and loss prevention services</span>
                    </li>
                    <li className="flex">
                      <Check className="h-6 w-6 text-[#00a86b] mr-2 flex-shrink-0" />
                      <span className="text-gray-700">Return-to-work programs to minimize claim costs</span>
                    </li>
                    <li className="flex">
                      <Check className="h-6 w-6 text-[#00a86b] mr-2 flex-shrink-0" />
                      <span className="text-gray-700">Coverage available in all 50 states</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Key Benefits Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Key Benefits of PEO Workers' Compensation</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gray-50 p-6 rounded-xl">
                <FileCheck className="h-12 w-12 text-[#00a86b] mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">Cost Efficiency</h3>
                <p className="text-gray-700">Benefit from the PEO's buying power to access more competitive rates than many businesses could obtain independently, especially for small to mid-sized companies.</p>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-xl">
                <ClipboardList className="h-12 w-12 text-[#00a86b] mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">Claims Management</h3>
                <p className="text-gray-700">Leave the complex paperwork and claims administration to experts who will ensure proper handling while advocating for your business interests.</p>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-xl">
                <Shield className="h-12 w-12 text-[#00a86b] mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">Risk Mitigation</h3>
                <p className="text-gray-700">Implement proven safety programs and training designed to reduce workplace accidents and lower your Experience Modification Rate (EMR) over time.</p>
              </div>
            </div>
          </div>
        </section>
        
        {/* CTA Section */}
        <section className="bg-gray-50 py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Ready to Simplify Your Workers' Compensation?</h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-8">
              The PEO App connects you with multiple vetted PEO providers who offer comprehensive workers' compensation solutions tailored to your business needs.
            </p>
            <Button asChild size="lg" className="bg-[#00a86b] hover:bg-[#008f5a] text-white rounded-full px-8">
              <Link to="/application">Get Multiple Quotes Now</Link>
            </Button>
            <p className="text-sm text-gray-500 mt-4">One application, multiple quotes. No obligation.</p>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default WorkersCompService;
